/**
 * Utility helpers for safe error handling in catch blocks.
 *
 * All catch parameters should be typed as `unknown`. These helpers
 * narrow the type so callers can safely extract messages, codes, etc.
 */

/** Extract a human-readable message from an unknown caught value. */
export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (typeof err === "object" && err !== null) {
    const obj = err as Record<string, unknown>;
    if (typeof obj.message === "string") return obj.message;
    // Cognito SDK sometimes throws plain objects like { error: "...", status: 400 }
    if (typeof obj.error === "string") return obj.error;
  }
  return "Unknown error";
}

/**
 * Well-known network error messages thrown by browsers (Safari, Chrome, Firefox).
 * Used to detect connectivity problems vs. server/auth errors.
 */
const NETWORK_ERROR_PATTERNS = [
  "Load failed",          // Safari
  "Failed to fetch",      // Chrome
  "NetworkError",         // Firefox
  "Network request failed",
  "Network Error",
  "TypeError: cancelled", // Safari abort
  "FETCH_ERROR",          // RTK Query network error
];

/** Check whether the caught error looks like a network/connectivity failure. */
export function isNetworkError(err: unknown): boolean {
  const msg = getErrorMessage(err);
  return NETWORK_ERROR_PATTERNS.some(
    (pattern) => msg.includes(pattern)
  );
}

/**
 * Map a caught auth error to a translation key suitable for
 * `t("auth.errors.<key>")`.  Returns `undefined` when the error
 * is not one of the known categories (caller decides fallback).
 */
export function getAuthErrorKey(err: unknown): string | undefined {
  // 1. Network errors (must check first — they can wrap any status)
  if (isNetworkError(err)) return "networkError";

  // 2. Cognito SDK errors surfaced via authService (have `.code`)
  if (isCognitoError(err)) {
    switch (err.code) {
      case "NotAuthorizedException":
      case "InvalidCredentials":
        return "wrongPassword";
      case "UserNotFoundException":
        return "userNotFound";
      case "LimitExceededException":
      case "TooManyRequestsException":
        return "tooManyAttempts";
      default:
        return undefined; // let caller handle specific codes
    }
  }

  // 3. Plain objects with { error, status } (non-Error throws from SDK)
  if (
    typeof err === "object" &&
    err !== null &&
    !(err instanceof Error) &&
    "error" in err
  ) {
    const obj = err as Record<string, unknown>;
    // If the error field itself is a known Cognito message, map it
    if (typeof obj.error === "string") {
      const lower = obj.error.toLowerCase();
      if (lower.includes("incorrect") || lower.includes("password") || lower.includes("not authorized")) {
        return "wrongPassword";
      }
      if (lower.includes("user") && lower.includes("not") && lower.includes("found")) {
        return "userNotFound";
      }
      if (lower.includes("too many") || lower.includes("limit")) {
        return "tooManyAttempts";
      }
    }
  }

  return undefined;
}

/**
 * Type guard: does the caught value look like a Cognito SDK error
 * (i.e. has a string `.code` property)?
 */
export function isCognitoError(
  err: unknown
): err is { code: string; message?: string; cognitoUser?: unknown } {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    typeof (err as Record<string, unknown>).code === "string"
  );
}

/**
 * Type guard: does the caught value look like an RTK Query
 * `FetchBaseQueryError`?
 *
 * RTK Query rejected values typically carry `.data` (the server body)
 * and optionally `.status`.
 */
export function isRtkQueryError(
  err: unknown
): err is { data?: Record<string, unknown>; status?: number } {
  return typeof err === "object" && err !== null && "data" in err;
}

/**
 * Type guard: is the error an AbortError (from AbortController)?
 */
export function isAbortError(err: unknown): boolean {
  return (
    err instanceof DOMException && err.name === "AbortError" ||
    (typeof err === "object" &&
      err !== null &&
      "name" in err &&
      (err as Record<string, unknown>).name === "AbortError")
  );
}
